import React, { useState, useContext } from 'react';
import axios, { baseURL } from '../axiosInstance';
import { UserContext } from '../UserContext';
import '../Pages.css';
import './StepperForm.css';

// Import form components
import PersonalInfoForm from './forms/PersonalInfoForm';
import ForeignDetailsForm from './forms/ForeignDetailsForm';
import InstitutionsForm from './forms/InstitutionsForm';
import AssociatesForm from './forms/AssociatesForm';
import ReviewForm from './forms/ReviewForm';
import ImportForm from './forms/ImportForm';

// Import constants
import { countries } from './constants';

const userHasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

// `mode` controls which capture form is shown. When passed, the in-page button
// row is suppressed because the Navbar's Data Capturer submenu handles routing.
//   'domestic' — Local PIP stepper
//   'foreign'  — Foreign PIP stepper
//   'import'   — CSV import flow
//   'upload-un'— UN Sanctions XML upload
//   'upload-uk'— UK Sanctions XML upload (FCDO / OFSI)
function DataCapturer({ mode }) {
  const { user } = useContext(UserContext);
  const canUploadUnSanctions = userHasPermission(user, 'un_sanctions.upload');
  const canUploadUkSanctions = userHasPermission(user, 'uk_sanctions.upload');

  // Initialize with Local PIP capture by default
  const [pipType, setPipType] = useState(mode === 'foreign' ? 'Foreign' : 'Local');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  // National ID removed

  const [associates, setAssociates] = useState([{ first_name: '', middle_name: '', last_name: '', relationship_type: '', national_id: '' }]);
  const [institutions, setInstitutions] = useState([{ institution_name: '', institution_type: '', position: '', start_date: '', end_date: '' }]);
  const [foreignDetails, setForeignDetails] = useState({ country: '', additional_notes: '' });
  const [pipResultOverview, setPipResultOverview] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);

  const [showImportForm, setShowImportForm] = useState(mode === 'import');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  // UN Sanctions XML upload state
  const [showUnUpload, setShowUnUpload] = useState(mode === 'upload-un');
  const [unFile, setUnFile] = useState(null);
  const [unUploading, setUnUploading] = useState(false);
  const [showUnConfirm, setShowUnConfirm] = useState(false);

  // UK Sanctions XML upload state (FCDO / OFSI). Same shape as UN.
  const [showUkUpload, setShowUkUpload] = useState(mode === 'upload-uk');
  const [ukFile, setUkFile] = useState(null);
  const [ukUploading, setUkUploading] = useState(false);
  const [showUkConfirm, setShowUkConfirm] = useState(false);

  // Stepper state - show stepper by default for PIP modes; hide for non-PIP modes.
  const isPipMode = mode === 'domestic' || mode === 'foreign' || !mode;
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showStepper, setShowStepper] = useState(isPipMode);
  const [validationErrors, setValidationErrors] = useState({});

  // Re-sync the visible form to the URL whenever the `mode` prop changes.
  // This guards against React-Router reusing the component instance across
  // sub-routes — without this, dismissing a form (e.g. clicking Cancel on
  // Upload UN) would persist when the user revisits via the menu.
  React.useEffect(() => {
    setShowImportForm(mode === 'import');
    setShowUnUpload(mode === 'upload-un');
    setShowUkUpload(mode === 'upload-uk');
    setShowStepper(mode === 'domestic' || mode === 'foreign' || !mode);
    setPipType(mode === 'foreign' ? 'Foreign' : (mode === 'domestic' || !mode) ? 'Local' : null);
    setCurrentStep(0);
    setCompletedSteps([]);
    setValidationErrors({});
  }, [mode]);

  // Define steps based on PIP type
  const getSteps = () => {
    const baseSteps = [
      { label: 'Personal Info', description: 'PIP personal information' },
      { label: 'Institutions', description: 'Institution affiliations' },
      { label: 'Associates', description: 'Related associates' },
      { label: 'Review', description: 'Review and submit' }
    ];

    if (pipType === 'Foreign') {
      baseSteps.splice(1, 0, { label: 'Foreign Details', description: 'Country and additional notes' });
    }

    return baseSteps;
  };

  const steps = getSteps();

  // Validation function for each step
  const validateStep = (stepIndex) => {
    const errors = {};

    if (stepIndex === 0) {
      // Personal Information validation
      if (!firstName.trim()) errors.firstName = 'First name is required';
      if (!lastName.trim()) errors.lastName = 'Last name is required';
      if (!pipResultOverview.trim()) errors.pipResultOverview = 'PIP Result Overview is required';
      // National ID validation removed
  // Reason removed from the form
    }

    if (pipType === 'Foreign' && stepIndex === 1) {
      // Foreign Details validation
      if (!foreignDetails.country.trim()) {
        errors.country = 'Country is required';
      } else if (!countries.includes(foreignDetails.country)) {
        errors.country = 'Please select a valid country from the list';
      }
    }

    const institutionStepIndex = pipType === 'Foreign' ? 2 : 1;
    if (stepIndex === institutionStepIndex) {
      // At least one institution is required
      const hasValidInstitution = institutions.some(inst => inst.institution_name.trim());
      if (!hasValidInstitution) {
        errors.institutions = 'At least one institution is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCompletedSteps([...completedSteps, currentStep]);
        setCurrentStep(currentStep + 1);
        setValidationErrors({});
      }
    } else {
      setMessage('❌ Please fill in all required fields');
      setShowMessagePopup(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
    }
  };

  const handleStepClick = (index) => {
    // Only allow navigation to completed steps or the current step
    if (index <= Math.max(...completedSteps, currentStep)) {
      // Validate current step before moving
      if (index > currentStep && !validateStep(currentStep)) {
        setMessage('❌ Please complete the current step before proceeding');
        setShowMessagePopup(true);
        return;
      }
      setCurrentStep(index);
      setValidationErrors({});
    }
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  const addAssociate = () => {
    setAssociates([...associates, { first_name: '', middle_name: '', last_name: '', relationship_type: '', national_id: '' }]);
  };

  const removeAssociate = (index) => {
    if (associates.length > 1) {
      const updated = associates.filter((_, idx) => idx !== index);
      setAssociates(updated);
    }
  };

  const addInstitution = () => {
    setInstitutions([...institutions, { institution_name: '', institution_type: '', position: '', start_date: '', end_date: '' }]);
  };

  const removeInstitution = (index) => {
    if (institutions.length > 1) {
      const updated = institutions.filter((_, idx) => idx !== index);
      setInstitutions(updated);
    }
  };

  const handleAssociateChange = (index, field, value) => {
    const updated = [...associates];
    updated[index][field] = value;
    setAssociates(updated);
  };

  const handleInstitutionChange = (index, field, value) => {
    const updated = [...institutions];
    updated[index][field] = value;
    setInstitutions(updated);
    // Clear institution error when user starts typing
    if (field === 'institution_name' && value.trim() && validationErrors.institutions) {
      setValidationErrors({ ...validationErrors, institutions: null });
    }
  };

  // National ID handler removed

  const handleCountryChange = (e) => {
    const val = e.target.value;
    setForeignDetails({ ...foreignDetails, country: val });
    setSuggestions(val ? countries.filter(c => c.toLowerCase().startsWith(val.toLowerCase())) : []);
    // Clear error when user starts typing
    if (validationErrors.country) {
      setValidationErrors({ ...validationErrors, country: null });
    }
  };

  const selectCountry = (country) => {
    setForeignDetails({ ...foreignDetails, country });
    setSuggestions([]);
    // Clear error when country is selected
    if (validationErrors.country) {
      setValidationErrors({ ...validationErrors, country: null });
    }
  };

  const closeMessagePopup = () => {
    setShowMessagePopup(false);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);

    // Final validation before submit
    if (pipType === 'Foreign' && !countries.includes(foreignDetails.country)) {
      setMessage('❌ Please select a valid country from the list');
      setShowMessagePopup(true);
      return;
    }

    setSubmitting(true);

    const pipData = {
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
  // national_id removed
      pip_type: pipType,
      is_foreign: pipType === 'Foreign',
      reason: pipResultOverview.trim(),
      pip_result_overview: pipResultOverview.trim(),
      associates: associates.filter(a => Object.values(a).some(v => v.trim())),
      institutions: institutions.filter(i => i.institution_name.trim()),
      foreign: pipType === 'Foreign' ? foreignDetails : null
    };

    try {
      const response = await axios.post('/pips', pipData);
      if (response.status !== 201 && response.status !== 200) throw new Error('Failed to save data');

      setMessage('✅ PIP successfully captured!');
      setShowMessagePopup(true);

  // Reset form
      setFirstName('');
      setMiddleName('');
      setLastName('');
      setPipResultOverview('');
  // nationalId reset removed
      setAssociates([{ first_name: '', middle_name: '', last_name: '', relationship_type: '', national_id: '' }]);
      setInstitutions([{ institution_name: '', institution_type: '', position: '', start_date: '', end_date: '' }]);
      setForeignDetails({ country: '', additional_notes: '' });
      setCurrentStep(0);
      setCompletedSteps([]);
      setValidationErrors({});

    } catch (err) {
      setMessage('❌ Error: ' + err.message);
      setShowMessagePopup(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Update the handleImport function
  const handleImport = async (e) => {
    if (e) e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);
    setImportProgress(null);

    if (!importFile || !importFile.name.toLowerCase().endsWith('.csv')) {
      setMessage('❌ Please select a valid CSV file.');
      setShowMessagePopup(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    // Declare progressInterval outside try block
    let progressInterval;

    try {
      setImporting(true);
      
      // Parse CSV on client side to get total count
      const fileText = await importFile.text();
      const lines = fileText.split('\n').filter(line => line.trim());
      const totalRecords = Math.max(lines.length - 1, 1); // Subtract header row, minimum 1
      
      // Initialize progress
      setImportProgress({
        current: 0,
        total: totalRecords,
        successful: 0,
        failed: 0,
        currentRecord: ''
      });

      // Simulate progress updates
      let currentProgress = 0;
      
      progressInterval = setInterval(() => {
        currentProgress++;
        if (currentProgress <= totalRecords) {
          // Try to parse the current line for name
          let currentName = '';
          if (lines[currentProgress]) {
            const fields = lines[currentProgress].split(',');
            currentName = `${fields[0] || ''} ${fields[2] || ''}`.trim();
          }
          
          setImportProgress(prev => ({
            ...prev,
            current: currentProgress,
            currentRecord: currentName || `Record ${currentProgress}`,
            // Simulate some successes/failures
            successful: Math.floor(currentProgress * 0.9),
            failed: Math.floor(currentProgress * 0.1)
          }));
        }
      }, 300); // Update every 300ms to make it visible

      // Make the actual request
      const response = await axios.post('/pips/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // We may receive either JSON (success) or CSV (errors) from the server
        responseType: 'arraybuffer',
        validateStatus: (status) => status >= 200 && status < 300
      });

      // Clear the interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // Handle dual-mode response: JSON (no failures) or CSV (failures)
      const contentType = (response.headers && response.headers['content-type']) || '';
      if (contentType.includes('text/csv')) {
        // Build download from CSV body
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = `import_errors_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Counts come in headers. If they can't be read, the error CSV
        // itself says how many rows failed (one line per failed row).
        const csvText = new TextDecoder('utf-8').decode(response.data);
        const csvFailed = Math.max(0, csvText.split('\n').filter((l) => l.trim() !== '').length - 1);
        const header = (name) => {
          const n = parseInt(response.headers[name], 10);
          return Number.isNaN(n) ? null : n;
        };
        const totalProcessed = header('x-import-total') ?? totalRecords;
        const failedCount = header('x-import-failed') ?? csvFailed;
        const successCount = header('x-import-success') ?? Math.max(0, totalProcessed - failedCount);

        setImportProgress({
          current: totalProcessed,
          total: totalProcessed,
          successful: successCount,
          failed: failedCount,
          currentRecord: 'Import completed with errors'
        });

        const summary = `${successCount === 0 ? '❌ Import failed' : '⚠️ Import completed with errors'}\n\nSuccessful: ${successCount}\nFailed: ${failedCount}\nTotal Processed: ${totalProcessed}\n\nA CSV with errors has been downloaded.`;
        setMessage(summary.trim());
        setShowMessagePopup(true);
        setImportFile(null);

        setTimeout(() => setImportProgress(null), 3000);
      } else {
        // Assume JSON success
        // JSON body is the API envelope { success, data: { total_processed,
        // success_count, failed_count, errors } } — arraybuffer responses
        // aren't unwrapped by the axios interceptor.
        const text = new TextDecoder('utf-8').decode(response.data);
        const parsed = JSON.parse(text);
        const result = (parsed && parsed.data) || parsed || {};
        const successCount = result.success_count || 0;
        const failedCount = result.failed_count || 0;
        const totalProcessed = result.total_processed ?? totalRecords;

        setImportProgress({
          current: totalProcessed,
          total: totalProcessed,
          successful: successCount,
          failed: failedCount,
          currentRecord: 'Import completed!'
        });

        const summary = `${successCount > 0 ? '✅ Import completed!' : '⚠️ Nothing was imported'}\n\nSuccessful: ${successCount}\nFailed: ${failedCount}\nTotal Processed: ${totalProcessed}`;

        setMessage(summary.trim());
        setShowMessagePopup(true);
        setImportFile(null);
        setTimeout(() => setImportProgress(null), 3000);
      }

    } catch (err) {
      // Now progressInterval is accessible here
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      console.error('Import error:', err);
      setMessage(`❌ Import failed: ${err.message}`);
      setShowMessagePopup(true);
      setImportProgress(null);
    } finally {
      setImporting(false);
      const fileInput = document.getElementById('csvFileInput');
      if (fileInput) fileInput.value = '';
    }
  };

  // ----- UN Sanctions XML upload -----
  // Step 1: form submit -> validate file and open the in-page confirm modal.
  const requestUnSanctionsUpload = (e) => {
    if (e) e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);

    if (!unFile || !unFile.name.toLowerCase().endsWith('.xml')) {
      setMessage('❌ Please select a valid XML file (e.g. consolidatedLegacyByNAME.xml).');
      setShowMessagePopup(true);
      return;
    }

    setShowUnConfirm(true);
  };

  // Step 2: user confirmed in the modal -> actually POST the file.
  const confirmUnSanctionsUpload = async () => {
    setShowUnConfirm(false);
    if (!unFile) return;

    const formData = new FormData();
    formData.append('file', unFile);

    try {
      setUnUploading(true);
      const response = await axios.post('/un-sanctions/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data || {};
      const indCount = data.individuals_count ?? 0;
      const entCount = data.entities_count ?? 0;

      setMessage(`✅ UN Sanctions list replaced.\n\nIndividuals: ${indCount}\nEntities: ${entCount}`);
      setShowMessagePopup(true);
      setUnFile(null);
      setShowUnUpload(false);
      const input = document.getElementById('unXmlFileInput');
      if (input) input.value = '';
    } catch (err) {
      const apiMsg = err.response?.data?.error?.message || err.response?.data?.error || err.message;
      setMessage(`❌ Upload failed: ${apiMsg}`);
      setShowMessagePopup(true);
    } finally {
      setUnUploading(false);
    }
  };

  // ----- UK Sanctions XML upload (FCDO / OFSI) -----
  // Mirrors the UN flow exactly — separate state + endpoint.
  const requestUkSanctionsUpload = (e) => {
    if (e) e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);

    if (!ukFile || !ukFile.name.toLowerCase().endsWith('.xml')) {
      setMessage('❌ Please select a valid XML file (e.g. UK-Sanctions-List.xml).');
      setShowMessagePopup(true);
      return;
    }

    setShowUkConfirm(true);
  };

  const confirmUkSanctionsUpload = async () => {
    setShowUkConfirm(false);
    if (!ukFile) return;

    const formData = new FormData();
    formData.append('file', ukFile);

    try {
      setUkUploading(true);
      const response = await axios.post('/uk-sanctions/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data || {};
      const indCount = data.individuals_count ?? 0;
      const entCount = data.entities_count ?? 0;

      setMessage(`✅ UK Sanctions list replaced.\n\nIndividuals: ${indCount}\nEntities: ${entCount}`);
      setShowMessagePopup(true);
      setUkFile(null);
      setShowUkUpload(false);
      const input = document.getElementById('ukXmlFileInput');
      if (input) input.value = '';
    } catch (err) {
      const apiMsg = err.response?.data?.error?.message || err.response?.data?.error || err.message;
      setMessage(`❌ Upload failed: ${apiMsg}`);
      setShowMessagePopup(true);
    } finally {
      setUkUploading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <PersonalInfoForm
            pipType={pipType}
            firstName={firstName}
            setFirstName={setFirstName}
            middleName={middleName}
            setMiddleName={setMiddleName}
            lastName={lastName}
            setLastName={setLastName}
            pipResultOverview={pipResultOverview}
            setPipResultOverview={setPipResultOverview}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        );

      case 1:
        if (pipType === 'Foreign') {
          return (
            <ForeignDetailsForm
              foreignDetails={foreignDetails}
              setForeignDetails={setForeignDetails}
              handleCountryChange={handleCountryChange}
              selectCountry={selectCountry}
              suggestions={suggestions}
              validationErrors={validationErrors}
            />
          );
        }
        // Fall through to institutions for Local PIPs

      case pipType === 'Foreign' ? 2 : 1:
        return (
          <InstitutionsForm
            institutions={institutions}
            handleInstitutionChange={handleInstitutionChange}
            addInstitution={addInstitution}
            removeInstitution={removeInstitution}
            validationErrors={validationErrors}
          />
        );

      case pipType === 'Foreign' ? 3 : 2:
        return (
          <AssociatesForm
            associates={associates}
            handleAssociateChange={handleAssociateChange}
            addAssociate={addAssociate}
            removeAssociate={removeAssociate}
          />
        );

      case pipType === 'Foreign' ? 4 : 3:
        return (
          <ReviewForm
            pipType={pipType}
            firstName={firstName}
            middleName={middleName}
            lastName={lastName}
            pipResultOverview={pipResultOverview}
            foreignDetails={foreignDetails}
            institutions={institutions}
            associates={associates}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      {/* In-page button row is shown only when no mode is set (legacy /DataCapturer/datacapturer route).
          The Navbar's Data Capturer submenu replaces it for the per-form routes. */}
      {!mode && (
        <div className="button-group">
          <button className={`export-button ${pipType === 'Local' && showStepper ? 'active-button' : ''}`} onClick={() => {
            setPipType('Local');
            setShowImportForm(false);
            setShowStepper(true);
            setCurrentStep(0);
            setCompletedSteps([]);
            setValidationErrors({});
          }}>
            + Capture Domestic PIP
          </button>
          <button className={`export-button ${pipType === 'Foreign' && showStepper ? 'active-button' : ''}`} onClick={() => {
            setPipType('Foreign');
            setShowImportForm(false);
            setShowStepper(true);
            setCurrentStep(0);
            setCompletedSteps([]);
            setValidationErrors({});
          }}>
            + Capture Foreign PIP
          </button>
          <button className={`export-button ${showImportForm ? 'active-button' : ''}`} onClick={() => {
            setShowImportForm(true);
            setShowStepper(false);
            setPipType(null);
          }}>
            + Import PIPs
          </button>
          {canUploadUnSanctions && (
            <button
              className={`export-button ${showUnUpload ? 'active-button' : ''}`}
              onClick={() => {
                setShowUnUpload(true);
                setShowImportForm(false);
                setShowStepper(false);
                setPipType(null);
              }}
            >
              + Upload UN Sanctions List
            </button>
          )}
        </div>
      )}

      {/* PIP stepper — only on PIP routes or the legacy no-mode landing */}
      {(!mode || mode === 'domestic' || mode === 'foreign') && showStepper && pipType && (
        <section className="form-section">
          <header className="form-section__head">
            <h4 className="form-section__title">{pipType === 'Foreign' ? 'Capture Foreign PIP' : 'Capture Domestic PIP'}</h4>
            <p className="form-section__subtitle">Politically Influential Person record (5-step capture).</p>
          </header>
          <div className="stepper-container">
            <div className="stepper-header">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`stepper-step ${
                    index === currentStep ? 'active' : ''
                  } ${completedSteps.includes(index) ? 'completed' : ''}`}
                  onClick={() => handleStepClick(index)}
                >
                  <div className="step-number">
                    {completedSteps.includes(index) ? '✓' : index + 1}
                  </div>
                  <div className="step-label">{step.label}</div>
                </div>
              ))}
            </div>

            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            <div className="stepper-content">
              {renderStepContent()}
            </div>

            <div className="stepper-navigation">
              <button
                className="step-button previous"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                Previous
              </button>

              {currentStep === steps.length - 1 ? (
                <button
                  className="step-button submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit PIP'}
                </button>
              ) : (
                <button
                  className="step-button next"
                  onClick={handleNext}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Import PIPs — only on the import route or legacy no-mode landing */}
      {(!mode || mode === 'import') && showImportForm && (
        <section className="form-section">
          <header className="form-section__head">
            <h4 className="form-section__title">Import PIPs</h4>
            <p className="form-section__subtitle">Bulk-import PIP records from a CSV file.</p>
          </header>
          <ImportForm
            importFile={importFile}
            setImportFile={setImportFile}
            handleImport={handleImport}
            importing={importing}
            importProgress={importProgress}
            setImportProgress={setImportProgress}
          />
        </section>
      )}

      {/* UK Sanctions XML upload (FCDO / OFSI) — mirrors UN flow exactly */}
      {(!mode || mode === 'upload-uk') && showUkUpload && canUploadUkSanctions && (
        <section className="form-section">
          <header className="form-section__head">
            <h4 className="form-section__title">Upload UK Sanctions XML</h4>
            <p className="form-section__subtitle">Replace the UK Sanctions list (transactional).</p>
          </header>
          <p style={{ color: 'var(--text-secondary,#607d8b)', marginBottom: '1rem', fontSize: '0.92rem' }}>
            Select the UK Sanctions list XML from FCDO (e.g. <code>UK-Sanctions-List.xml</code>).
            Uploading will <strong>replace all existing UK Sanctions data</strong>. The import runs in a single
            transaction — if anything fails, the previous data is kept.
          </p>
          <form onSubmit={requestUkSanctionsUpload}>
            <div className="form-group">
              <input
                id="ukXmlFileInput"
                type="file"
                accept=".xml,application/xml,text/xml"
                onChange={(e) => setUkFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                disabled={ukUploading}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="export-button" disabled={!ukFile || ukUploading}>
                {ukUploading ? 'Uploading…' : 'Upload & Replace'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => { setShowUkUpload(false); setUkFile(null); }}
                disabled={ukUploading}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {/* UN Sanctions XML upload — only on the upload route or legacy no-mode landing */}
      {(!mode || mode === 'upload-un') && showUnUpload && canUploadUnSanctions && (
        <section className="form-section">
          <header className="form-section__head">
            <h4 className="form-section__title">Upload UN Sanctions XML</h4>
            <p className="form-section__subtitle">Replace the UN Consolidated Sanctions list (transactional).</p>
          </header>
          <p style={{ color: 'var(--text-secondary,#607d8b)', marginBottom: '1rem', fontSize: '0.92rem' }}>
            Select the UN Consolidated Sanctions list XML (e.g. <code>consolidatedLegacyByNAME.xml</code>).
            Uploading will <strong>replace all existing UN Sanctions data</strong>. The import runs in a single
            transaction — if anything fails, the previous data is kept.
          </p>
          <form onSubmit={requestUnSanctionsUpload}>
            <div className="form-group">
              <input
                id="unXmlFileInput"
                type="file"
                accept=".xml,application/xml,text/xml"
                onChange={(e) => setUnFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                disabled={unUploading}
              />
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="export-button"
                disabled={!unFile || unUploading}
              >
                {unUploading ? 'Uploading…' : 'Upload & Replace'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => { setShowUnUpload(false); setUnFile(null); }}
                disabled={unUploading}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {showMessagePopup && (
        <div className="message-popup">
          <div className="message-popup-content">
            <pre style={{ whiteSpace: 'pre-wrap' }}>{message}</pre>
            <button onClick={closeMessagePopup} className="close-popup-button">
              Close
            </button>
          </div>
        </div>
      )}

      {showUnConfirm && (
        <div className="message-popup">
          <div className="message-popup-content">
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>
              Replace UN Sanctions list?
            </h3>
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
{`This will REPLACE all stored UN Sanctions data with the contents of
"${unFile ? unFile.name : ''}".

If the upload fails the existing data is kept (transactional).`}
            </pre>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowUnConfirm(false)}
                disabled={unUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="export-button"
                onClick={confirmUnSanctionsUpload}
                disabled={unUploading}
              >
                {unUploading ? 'Uploading…' : 'Upload & Replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUkConfirm && (
        <div className="message-popup">
          <div className="message-popup-content">
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>
              Replace UK Sanctions list?
            </h3>
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
{`This will REPLACE all stored UK Sanctions data with the contents of
"${ukFile ? ukFile.name : ''}".

If the upload fails the existing data is kept (transactional).`}
            </pre>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowUkConfirm(false)}
                disabled={ukUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="export-button"
                onClick={confirmUkSanctionsUpload}
                disabled={ukUploading}
              >
                {ukUploading ? 'Uploading…' : 'Upload & Replace'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataCapturer;
